import { Children, FC, ReactNode, isValidElement, useLayoutEffect, useRef } from "react";

import { BaseProps } from "../../BaseProps";
import { createComponentStyleHook } from "../../tokens";
import { debounce } from "../../utilities/debounce";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { withSubComponents } from "../../utilities/withSubComponents";
import { Group } from "./Group";
import { Item } from "./Item";

import legacyStyles from "./legacy/styles.module.scss";
import tokenStyles from "./styles.module.css";

/** The `SidebarNav` component properties. */
export interface SidebarNavProps extends BaseProps {
  /** The component children. */
  readonly children?: ReactNode;

  /** The accessible label for the navigation region. */
  readonly ariaLabel?: string;
}

/** The `SidebarNav` sub components. */
export interface SideBarNavSubComponents {
  /** Represents a group of items in the sidebar. */
  readonly Group: typeof Group;

  /** Represents an item within the sidebar. */
  readonly Item: typeof Item;
}

const useStyles = createComponentStyleHook({
  legacyStyles,
  tokenStyles,
  componentTokens: "sidebar_nav",
});

/* Scoped per nav so sidebars from different shells and modules in the same tab do not overwrite each other. */
const SCROLL_STORAGE_KEY_PREFIX = "capitalrx.sidebarNav.scrollTop";

const scrollPanelToCurrent = (container: HTMLElement, options: { center?: boolean } = {}) => {
  const { center = false } = options;
  const current = container.querySelector('[aria-current="page"], .active');
  if (!current) {
    return false;
  }

  const containerRect = container.getBoundingClientRect();
  const currentRect = current.getBoundingClientRect();

  const offsetTop = currentRect.top - containerRect.top + container.scrollTop;

  if (center) {
    container.scrollTop = offsetTop - container.clientHeight / 2 + current.clientHeight / 2;
    return true;
  }

  const viewTop = container.scrollTop;
  const viewBottom = viewTop + container.clientHeight;

  if (offsetTop < viewTop) {
    container.scrollTop = offsetTop;
  } else if (offsetTop + current.clientHeight > viewBottom) {
    container.scrollTop = offsetTop + current.clientHeight - container.clientHeight;
  }

  return true;
};

/* Bounded at the document scrollers so the nav never takes over the page scroll position. */
const findScrollableParent = (element: HTMLElement): HTMLElement | null => {
  let current = element.parentElement;
  while (current && current !== document.body && current !== document.documentElement) {
    const { overflow, overflowY } = window.getComputedStyle(current);
    if (overflow === "auto" || overflow === "scroll" || overflowY === "auto" || overflowY === "scroll") {
      return current;
    }
    current = current.parentElement;
  }
  return null;
};

const restoreScrollTop = (container: HTMLElement, storageKey: string): boolean => {
  const savedScrollTop = Number.parseInt(sessionStorage.getItem(storageKey) ?? "", 10);
  if (!Number.isFinite(savedScrollTop)) {
    return false;
  }

  container.scrollTop = savedScrollTop;
  return true;
};

/** The SideBar navigation component. */
export const SidebarNav = withSubComponents<FC<SidebarNavProps>, SideBarNavSubComponents>(
  {
    Group,
    Item,
  },
  (props) => {
    const { children, ariaLabel = "Sidebar navigation", testId } = props;
    const { mode, styles, StyleModeBoundary } = useStyles(props);

    const navRef = useRef<HTMLElement>(null);
    const storageKey = `${SCROLL_STORAGE_KEY_PREFIX}:${testId ?? ariaLabel}`;

    /* Mount only: the saved offset is what carries the position across remounts, so re-running would undo it. */
    useLayoutEffect(() => {
      if (!navRef.current) return;

      const scrollContainer = findScrollableParent(navRef.current) ?? navRef.current;

      if (restoreScrollTop(scrollContainer, storageKey)) {
        scrollPanelToCurrent(scrollContainer);
      } else {
        scrollPanelToCurrent(scrollContainer, { center: true });
      }

      const handleScroll = debounce(() => {
        sessionStorage.setItem(storageKey, scrollContainer.scrollTop.toString());
      });

      scrollContainer.addEventListener("scroll", handleScroll, { passive: true });

      return () => {
        scrollContainer.removeEventListener("scroll", handleScroll);
      };
    }, [storageKey]);

    const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
      className: [styles.SidebarNav, "sidebar-nav"],
    });

    const validElements = Children.map(children, (child) => {
      if (isValidElement(child)) return child;
      return null;
    })?.filter((child) => !!child);

    return (
      <StyleModeBoundary mode={mode}>
        <nav {...baseHtmlProps} aria-label={ariaLabel} ref={navRef}>
          <ul>
            {validElements?.map((element, index) => {
              let hasSeparator = index < validElements.length - 1;
              const nextItem = validElements[index + 1];

              if (element.type === Item && nextItem?.type === Item) hasSeparator = false;

              return (
                <li key={index}>
                  {element}
                  {hasSeparator && <div className={styles.separator} aria-hidden="true" />}
                </li>
              );
            })}
          </ul>
        </nav>
      </StyleModeBoundary>
    );
  },
);
